import { useEffect, useRef, useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { getVesselSession, clearVesselSession } from "../lib/vesselAuth";
import {
  vesselPortalApi,
  VesselThread,
  VesselThreadMessage,
  VesselTransfer,
} from "../api/vesselPortal";
import { contactsApi, Contact } from "../api/contacts";

const BASE_URL = (import.meta as any).env?.VITE_API_URL ?? "";

// ── helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = (now.getTime() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function isExpired(iso: string): boolean {
  return new Date(iso) < new Date();
}

function statusColor(s: VesselTransfer["status"]): string {
  if (s === "delivered") return "bg-green-100 text-green-700";
  if (s === "in_transit") return "bg-blue-100 text-blue-700";
  if (s === "cancelled") return "bg-red-100 text-red-700";
  return "bg-yellow-100 text-yellow-700";
}

// ── VesselPortal ──────────────────────────────────────────────────────────────

export default function VesselPortal() {
  const navigate = useNavigate();
  const session = getVesselSession();

  useEffect(() => {
    if (!session) navigate("/vessel/login", { replace: true });
  }, [session, navigate]);

  const [tab, setTab] = useState<"messages" | "downloads">("messages");

  // ── Messages state ──────────────────────────────────────────────
  const [threads, setThreads] = useState<VesselThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<VesselThread | null>(null);
  const [threadMsgs, setThreadMsgs] = useState<VesselThreadMessage[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [mobileView, setMobileView] = useState<"list" | "thread">("list");

  // ── Compose state ───────────────────────────────────────────────
  const [showCompose, setShowCompose] = useState(false);
  const [composeContactId, setComposeContactId] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeSending, setComposeSending] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);

  // ── Reply state ─────────────────────────────────────────────────
  const [replyBody, setReplyBody] = useState("");
  const [replySending, setReplySending] = useState(false);

  // ── Downloads state ─────────────────────────────────────────────
  const [transfers, setTransfers] = useState<VesselTransfer[]>([]);
  const [downloadsLoading, setDownloadsLoading] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ── Load contacts once ──────────────────────────────────────────
  useEffect(() => {
    contactsApi.list().then(setContacts).catch(console.error);
  }, []);

  // ── Load threads ────────────────────────────────────────────────
  function loadThreads() {
    vesselPortalApi.listThreads().then(setThreads).catch(console.error);
  }

  useEffect(loadThreads, []);

  // ── Load downloads when tab switches ────────────────────────────
  useEffect(() => {
    if (tab === "downloads" && transfers.length === 0) {
      setDownloadsLoading(true);
      vesselPortalApi
        .listDownloads()
        .then(setTransfers)
        .catch(console.error)
        .finally(() => setDownloadsLoading(false));
    }
  }, [tab]);

  // ── Scroll to bottom when messages load ─────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [threadMsgs]);

  if (!session) return null;

  // ── Select a thread ─────────────────────────────────────────────
  async function selectThread(thread: VesselThread) {
    setSelectedThread(thread);
    setThreadMsgs([]);
    setReplyBody("");
    setMobileView("thread");
    try {
      const data = await vesselPortalApi.getThread(thread.id);
      setThreadMsgs(data.messages);
      // Optimistically clear unread count in list
      setThreads((ts) =>
        ts.map((t) => (t.id === thread.id ? { ...t, unread_count: 0 } : t))
      );
    } catch (e) {
      console.error(e);
    }
  }

  // ── Send reply ──────────────────────────────────────────────────
  async function sendReply(e: FormEvent) {
    e.preventDefault();
    if (!selectedThread || !replyBody.trim()) return;
    setReplySending(true);
    try {
      await vesselPortalApi.sendMessage({
        thread_id: selectedThread.id,
        body: replyBody.trim(),
      });
      const data = await vesselPortalApi.getThread(selectedThread.id);
      setThreadMsgs(data.messages);
      setReplyBody("");
      loadThreads();
    } catch (e) {
      console.error(e);
    } finally {
      setReplySending(false);
    }
  }

  // ── Acknowledge message ─────────────────────────────────────────
  async function acknowledge(msgId: string) {
    try {
      await vesselPortalApi.acknowledge(msgId);
      setThreadMsgs((ms) =>
        ms.map((m) =>
          m.id === msgId ? { ...m, acknowledged_at: new Date().toISOString() } : m
        )
      );
      loadThreads();
    } catch (e) {
      console.error(e);
    }
  }

  // ── Send new message ────────────────────────────────────────────
  async function sendCompose(e: FormEvent) {
    e.preventDefault();
    if (!composeContactId || !composeBody.trim()) return;
    setComposeSending(true);
    setComposeError(null);
    try {
      const res = await vesselPortalApi.sendMessage({
        contact_id: composeContactId,
        subject: composeSubject.trim() || undefined,
        body: composeBody.trim(),
      });
      setShowCompose(false);
      setComposeContactId("");
      setComposeSubject("");
      setComposeBody("");
      await loadThreads();
      // Open the new thread
      const newThreads = await vesselPortalApi.listThreads();
      setThreads(newThreads);
      const newThread = newThreads.find((t) => t.id === res.thread_id);
      if (newThread) selectThread(newThread);
    } catch (err) {
      setComposeError(err instanceof Error ? err.message : "Failed to send");
    } finally {
      setComposeSending(false);
    }
  }

  function signOut() {
    clearVesselSession();
    navigate("/vessel/login");
  }

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen bg-gray-50">

      {/* ── Top bar ──────────────────────────────────────────────── */}
      <header className="shrink-0 bg-[#0f1f3d] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold tracking-widest text-blue-300 uppercase">SeaVault</span>
          <span className="text-white/30">|</span>
          <div>
            <span className="font-semibold text-sm">{session.name}</span>
            {session.imo_number && (
              <span className="ml-2 text-xs text-blue-200">IMO {session.imo_number}</span>
            )}
          </div>
        </div>
        <button
          onClick={signOut}
          className="text-xs text-blue-300 hover:text-white transition-colors"
        >
          Sign out
        </button>
      </header>

      {/* ── Tab bar ──────────────────────────────────────────────── */}
      <div className="shrink-0 bg-[#0f1f3d] border-b border-white/10 flex">
        {(["messages", "downloads"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-2.5 text-sm font-medium capitalize transition-colors ${
              tab === t
                ? "text-white border-b-2 border-blue-400"
                : "text-blue-300 hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ── Messages tab ─────────────────────────────────────────── */}
      {tab === "messages" && (
        <div className="flex flex-1 overflow-hidden">

          {/* Thread list — hidden on mobile when viewing a thread */}
          <aside
            className={`${
              mobileView === "thread" ? "hidden" : "flex"
            } md:flex flex-col w-full md:w-80 shrink-0 border-r bg-white overflow-hidden`}
          >
            <div className="px-3 py-3 border-b">
              <button
                onClick={() => setShowCompose(true)}
                className="w-full rounded-lg bg-[#1d5199] text-white text-sm font-semibold py-2 hover:bg-[#174680] transition-colors"
              >
                + New Message
              </button>
            </div>

            <div className="flex-1 overflow-y-auto divide-y">
              {threads.length === 0 && (
                <p className="text-center text-sm text-gray-400 py-12">No messages yet</p>
              )}
              {threads.map((thread) => (
                <button
                  key={thread.id}
                  onClick={() => selectThread(thread)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                    selectedThread?.id === thread.id ? "bg-blue-50" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {thread.unread_count > 0 && (
                          <span className="shrink-0 w-2 h-2 rounded-full bg-blue-500" />
                        )}
                        <span className="font-medium text-sm text-gray-900 truncate">
                          {thread.contact_name ?? "Shore Office"}
                        </span>
                      </div>
                      {thread.contact_role && (
                        <p className="text-xs text-gray-500 truncate">{thread.contact_role}</p>
                      )}
                      <p className="text-xs text-gray-600 mt-0.5 truncate">
                        {thread.subject}
                      </p>
                      {thread.last_body && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                          {thread.last_sender_type === "vessel" ? "You: " : ""}
                          {thread.last_body.slice(0, 60)}
                        </p>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      {thread.last_message_at && (
                        <span className="text-xs text-gray-400">{formatTime(thread.last_message_at)}</span>
                      )}
                      {thread.unread_count > 0 && (
                        <span className="block mt-1 ml-auto w-5 h-5 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">
                          {thread.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* Thread view — full screen on mobile when active */}
          <main
            className={`${
              mobileView === "list" ? "hidden" : "flex"
            } md:flex flex-col flex-1 overflow-hidden`}
          >
            {selectedThread ? (
              <>
                {/* Thread header */}
                <div className="shrink-0 bg-white border-b px-4 py-3 flex items-center gap-3">
                  <button
                    onClick={() => setMobileView("list")}
                    className="md:hidden text-gray-500 hover:text-gray-800 text-lg leading-none"
                    aria-label="Back"
                  >
                    ←
                  </button>
                  <div>
                    <p className="font-semibold text-sm text-gray-900">
                      {selectedThread.contact_name ?? "Shore Office"}
                    </p>
                    {selectedThread.contact_role && (
                      <p className="text-xs text-gray-500">{selectedThread.contact_role}</p>
                    )}
                    <p className="text-xs text-gray-400">{selectedThread.subject}</p>
                  </div>
                </div>

                {/* Message bubbles */}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                  {threadMsgs.map((msg) => {
                    const isVessel = msg.sender_type === "vessel";
                    return (
                      <div
                        key={msg.id}
                        className={`flex flex-col ${isVessel ? "items-end" : "items-start"}`}
                      >
                        <div
                          className={`max-w-xs sm:max-w-sm rounded-2xl px-4 py-2.5 text-sm ${
                            isVessel
                              ? "bg-[#1d5199] text-white rounded-br-sm"
                              : "bg-gray-100 text-gray-800 rounded-bl-sm"
                          }`}
                        >
                          {msg.body}
                        </div>
                        <div className="flex items-center gap-2 mt-1 px-1">
                          <span className="text-xs text-gray-400">
                            {msg.sender_name ?? (isVessel ? "Vessel" : "Shore")} · {formatTime(msg.created_at)}
                          </span>
                          {!isVessel && !msg.acknowledged_at && (
                            <button
                              onClick={() => acknowledge(msg.id)}
                              className="text-xs text-[#1d5199] hover:underline font-medium"
                            >
                              Acknowledge
                            </button>
                          )}
                          {!isVessel && msg.acknowledged_at && (
                            <span className="text-xs text-gray-400">✓ Acknowledged</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply box */}
                <form onSubmit={sendReply} className="shrink-0 border-t bg-white p-3 flex gap-2 items-end">
                  <textarea
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Type a reply…"
                    rows={2}
                    className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (replyBody.trim()) sendReply(e as any);
                      }
                    }}
                  />
                  <button
                    type="submit"
                    disabled={replySending || !replyBody.trim()}
                    className="shrink-0 rounded-lg bg-[#1d5199] text-white px-4 py-2 text-sm font-semibold hover:bg-[#174680] disabled:opacity-50 transition-colors"
                  >
                    {replySending ? "…" : "Send"}
                  </button>
                </form>
              </>
            ) : (
              <div className="hidden md:flex flex-1 items-center justify-center text-gray-400 text-sm">
                Select a conversation
              </div>
            )}
          </main>
        </div>
      )}

      {/* ── Downloads tab ─────────────────────────────────────────── */}
      {tab === "downloads" && (
        <div className="flex-1 overflow-y-auto px-4 py-6 max-w-3xl mx-auto w-full">
          {downloadsLoading && (
            <p className="text-sm text-gray-400 text-center py-12">Loading…</p>
          )}
          {!downloadsLoading && transfers.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-12">No documents received yet</p>
          )}
          <div className="space-y-3">
            {transfers.map((t) => {
              const expired = isExpired(t.expires_at);
              const canDownload = !!t.status && t.status !== "cancelled" && !expired;
              return (
                <div key={t.id} className="bg-white rounded-xl border p-4 flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm text-gray-900 truncate">{t.description}</p>
                      <span className="shrink-0 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">
                        Document
                      </span>
                      <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(t.status)}`}>
                        {t.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                      <span>Sent {formatTime(t.created_at)}</span>
                      <span className={expired ? "text-red-500 font-medium" : ""}>
                        Expires {new Date(t.expires_at).toLocaleDateString()}
                        {expired ? " (expired)" : ""}
                      </span>
                    </div>
                  </div>
                  <a
                    href={canDownload ? `${BASE_URL}/public/download/${t.download_token}/file` : undefined}
                    download
                    className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                      canDownload
                        ? "bg-[#1d5199] text-white hover:bg-[#174680]"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed pointer-events-none"
                    }`}
                    aria-disabled={!canDownload}
                  >
                    Download
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Compose modal ─────────────────────────────────────────── */}
      {showCompose && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowCompose(false); }}
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b bg-[#0f1f3d]">
              <h2 className="text-sm font-semibold text-white">New Message</h2>
              <button
                onClick={() => setShowCompose(false)}
                className="text-blue-300 hover:text-white text-xl leading-none"
              >
                &times;
              </button>
            </div>

            {composeError && (
              <div className="mx-5 mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
                {composeError}
              </div>
            )}

            <form onSubmit={sendCompose} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">
                  Recipient <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={composeContactId}
                  onChange={(e) => setComposeContactId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                >
                  <option value="">Select contact…</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.role_name ? ` — ${c.role_name}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">
                  Subject
                </label>
                <input
                  type="text"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Optional subject"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={4}
                  value={composeBody}
                  onChange={(e) => setComposeBody(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                  placeholder="Write your message…"
                />
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowCompose(false)}
                  className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={composeSending || !composeContactId || !composeBody.trim()}
                  className="flex-1 rounded-lg bg-[#1d5199] py-2 text-sm font-semibold text-white hover:bg-[#174680] disabled:opacity-50 transition-colors"
                >
                  {composeSending ? "Sending…" : "Send"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
